import { Sound } from 'feature/widget/Sound'
import { ym } from 'feature/yama'
import { makeAutoObservable } from 'mobx'
import { demo } from 'page/catalog/store/CatalogStore'
import { comboCartStore } from 'page/catalog/store/ComboCartStore'
import { ComboItemsStore } from 'page/catalog/store/ComboItemsStore'
import { parseIntFinite } from 'util/number'
import { loadJson } from 'util/storage'
import { generateUuid } from 'util/uuid'

const ACTIONS = ['resize', 'scroll', 'mousemove', 'mousedown', 'touchstart', 'touchmove', 'touchend', 'keypress']

export class SessionStore {
  picture = ''
  paused = false
  active = true
  timer = 0

  started = false
  start_time = 0
  session_id = ''
  private timeout: TimeoutId | null = null
  private sound = new Sound('https://imgs.dresscode.ai/window/tick-6cb5.mp3', 0.2)

  constructor() {
    makeAutoObservable(this)
  }

  start(picture: string | null) {
    this.sound.ensure()
    this.picture = picture ?? ''
    if (this.started) return
    this.session_id = generateUuid()
    ym('session_start', { session_id: this.session_id })
    this.started = true
    this.start_time = Date.now()
    this.paused = false
    this.active = true
    for (const action of ACTIONS) {
      document.addEventListener(action, this.onAction)
    }
    this.onAction()
  }

  stop() {
    const start = this.start_time
    const duration = start ? Math.max(1, Math.round((Date.now() - start) / 1000)) : 0
    ym('session_end', { session_id: this.session_id, duration })
    this.started = false
    this.start_time = 0
    this.picture = ''
    this.paused = false
    this.active = true
    this.session_id = ''
    this.clearTimeout()
    for (const action of ACTIONS) {
      document.removeEventListener(action, this.onAction)
    }
    comboCartStore.clearAll()
    ComboItemsStore.clearCache()
    demo.it.restart()
    window.scrollTo(0, 0)
  }

  cancel() {
    this.active = true
    if (!this.started) return
    ym('session_continue', { session_id: this.session_id })
    this.setTimeout(this.onInactive, this.inactive_timeout)
  }

  pause(pause: boolean) {
    this.paused = pause
    this.clearTimeout()
    if (pause) return
    this.setTimeout(this.onInactive, this.inactive_timeout)
  }

  private readonly onAction = () => {
    if (this.paused) return
    if (!this.active) return
    this.setTimeout(this.onInactive, this.inactive_timeout)
  }

  private readonly onInactive = () => {
    const user_id = loadJson('outfits.user_id')
    if (user_id) return
    this.active = false
    this.timer = Math.trunc(this.logout_timeout / 1000)
    this.setTimeout(this.onLogoutTick, 1000)
  }

  private readonly onLogoutTick = () => {
    if (this.timer > 1) {
      this.sound.play()
    }
    this.timer = Math.max(0, this.timer - 1)
    this.setTimeout(this.onLogoutTick, 1000)
  }

  private clearTimeout() {
    clearTimeout(this.timeout ?? undefined)
  }

  private setTimeout(callback: () => void, ms: number) {
    this.clearTimeout()
    this.timeout = setTimeout(callback, ms)
  }

  private get inactive_timeout(): number {
    const debug = parseIntFinite(localStorage.getItem('debug_session_timeout'))
    if (debug) return debug
    const timeout = demo.optional?.product.inactive_timeout
    return timeout ? timeout * 1000 : 120_000
  }

  private get logout_timeout(): number {
    const timeout = demo.optional?.product.logout_timeout
    return timeout ? timeout * 1000 : 30_000
  }
}

export const session = new SessionStore()
